import React, { useContext, useEffect, useState } from "react";
import AnimationIconComponent from "../AnimationIconComponent";
import { toast } from "react-toastify";
import { getSingleProdWithImages } from "../../services/get-single-prod-with-photos";
import "./index.scss";
import { UserContext } from "../context/userContext";
import WizardSteps from "../WizardSteps";

export interface ImageSlot {
  _id?: string;                // ✅ add id
  file: File | null;
  preview: string | null;
  deleted: boolean;
  fromServer: boolean;
}

const SLOTS = [0, 1, 2, 3];

function ModalAddProductPhoto({
  setStep,
  SubmitProduct,
  editMode,
  productsValues,
  closeModal,
  loadingAdd
}: any) {

  const [slots, setSlots] = useState<ImageSlot[]>(
    Array.from({ length: 4 }, () => ({
      _id: undefined,
      file: null,
      preview: null,
      deleted: false,
      fromServer: false,
    }))
  );

  const { networkError, setNetworkError } = useContext(UserContext);

  // =========================
  // FETCH PRODUCT WITH PHOTOS
  // =========================
  const doGetSingleProd = () => {
    getSingleProdWithImages(productsValues?._id)
      .then((response) => {
        if (response?.status === 500) return setNetworkError(true);
        if (!response?.success) return toast.error(response?.message);

        const serverPhotos = response?.content?.photos || [];

        const filledSlots: ImageSlot[] = Array.from({ length: 4 }, (_, i) => {
          const p = serverPhotos[i];

          if (!p) {
            return {
              _id: undefined,
              file: null,
              preview: null,
              deleted: false,
              fromServer: false,
            };
          }

          return {
            _id: p._id, // ✅ store id
            file: null,
            preview: `data:${p.contentType};base64,${p.data}`,
            deleted: false,
            fromServer: true,
          };
        });

        setSlots(filledSlots);
      })
      .catch((error) => toast.error(error));
  };

  useEffect(() => {
    if (editMode) doGetSingleProd();
  }, []);

  // =========================
  // CHANGE IMAGE
  // =========================
const onChangeImage = (slotIndex: number, file: File) => {
  const previewUrl = URL.createObjectURL(file);

  setSlots((prev) => {
    const updated = [...prev];

    updated[slotIndex] = {
      ...updated[slotIndex],  // 🔥 preserve id
      file,
      preview: previewUrl,
      deleted: false,
      fromServer: false,
    };

    return updated;
  });
};


  // =========================
  // DELETE IMAGE
  // =========================
const onDeleteImage = (slotIndex: number) => {
  setSlots((prev) => {
    const updated = [...prev];

    updated[slotIndex] = {
      ...updated[slotIndex],   // 🔥 preserve _id
      file: null,
      preview: null,
      deleted: true,
    };

    return updated;
  });
};


  // =========================
  // SAVE
  // =========================
  // 🔥 Collect deleted image IDs
  const deletedImageIds = slots
    .filter((slot) => slot.deleted && slot._id)
    .map((slot) => slot._id as string);

  // 🔥 Collect new images
  const newImages = slots
    .filter((slot) => slot.file)
    .map((slot) => slot.file as File);

  const remainingPhotos = slots.filter(
    (slot) => slot.preview && !slot.deleted,
  ).length;

  const handleSave = () => {
    SubmitProduct({
      deletedImageIds,
      newImages,
    });
  };

  return (
    <div className="add-photo-product-body-container">
      <div className="add-photo-header">
        <div>
          <h3>{editMode ? "Modifier les photos" : "Ajouter des photos"}</h3>
          <p className="add-photo-subtitle">
            {editMode
              ? "Mettez à jour les visuels du produit."
              : "Dernière étape avant l'enregistrement du produit."}
          </p>
        </div>
        <i className="bx bx-x" style={{ fontSize: 24 }} onClick={closeModal} />
      </div>

      <WizardSteps current={2} />

      <div className="image-grid-wrapper">
        {SLOTS.map((slotIndex) => (
          <AnimationIconComponent
            key={slotIndex}
            slot={slots[slotIndex]}
            slotIndex={slotIndex}
            onChangeImage={onChangeImage}
            onDeleteImage={onDeleteImage}
          />
        ))}
      </div>

      <div className="footer-modal-add-photos">
        <button
          className="btn-back"
          onClick={() => setStep("first")}
          disabled={loadingAdd}
        >
          <i className="bx bx-chevron-left" /> Retour
        </button>

        <div className="footer-right">
          <span className="photo-count">
            {editMode
              ? `${remainingPhotos} photo(s)`
              : `${newImages.length} photo(s) sélectionnée(s)`}
          </span>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={loadingAdd}
          >
            {loadingAdd
              ? "Enregistrement…"
              : editMode
              ? "Enregistrer les modifications"
              : "Enregistrer le produit"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ModalAddProductPhoto;
